import {
  categoryMatchesKind,
  codeSourceAllowed,
  ITEM_CODE_SOURCES,
  ITEM_CODE_SYSTEMS,
  SERVICE_ITEM_CATEGORIES,
  SERVICE_ITEM_KINDS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, reasonSchema, RECORD_STATUSES } from './clinical-common.ts';
import { amountSchema } from './money.ts';

// The service catalogue: one list of everything a facility charges for, services and
// stock items alike (docs/plans/phase1-finance-catalogue.md §2.1–2.4, §3.3). Catalogue
// data is not patient data, so item search may use a query string.

export { ITEM_CODE_SOURCES, ITEM_CODE_SYSTEMS, SERVICE_ITEM_CATEGORIES, SERVICE_ITEM_KINDS };

export const ACTIVE_STATUSES = ['active', 'inactive'] as const;

const itemCodeTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._/-]*$/, 'Use letters, digits, dots, dashes and slashes only');

export const serviceItemSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  kind: z.enum(SERVICE_ITEM_KINDS),
  category: z.enum(SERVICE_ITEM_CATEGORIES),
  billingUnit: z.string(),
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
});

export type ServiceItemView = z.infer<typeof serviceItemSchema>;

/** A row in the catalogue table, with the price in the list the table is showing. */
export const serviceItemRowSchema = serviceItemSchema.extend({
  /** Null when the item has no price in the chosen list right now. Never read as zero. */
  price: amountSchema.nullable(),
});

export const serviceItemCodeSchema = z.object({
  id: z.uuid(),
  system: z.enum(ITEM_CODE_SYSTEMS),
  systemUri: z.string().nullable(),
  systemVersion: z.string(),
  code: z.string(),
  display: z.string(),
  source: z.enum(ITEM_CODE_SOURCES),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  createdAt: z.iso.datetime(),
  createdBy: actorSchema,
});

export const itemAvailabilitySchema = z.object({
  facilityId: z.uuid(),
  facilityName: z.string(),
  offered: z.boolean(),
  reason: z.string().nullable(),
});

export const currentPriceSchema = z.object({
  priceListId: z.uuid(),
  priceListName: z.string(),
  priceId: z.uuid().nullable(),
  price: amountSchema.nullable(),
  effectiveFrom: z.iso.datetime().nullable(),
  /** A change already scheduled to start later, if any. */
  scheduled: z
    .object({ priceId: z.uuid(), price: amountSchema, effectiveFrom: z.iso.datetime() })
    .nullable(),
});

export const serviceItemDetailSchema = serviceItemSchema.extend({
  codes: z.array(serviceItemCodeSchema),
  availability: z.array(itemAvailabilitySchema),
  prices: z.array(currentPriceSchema),
});

export type ServiceItemDetailView = z.infer<typeof serviceItemDetailSchema>;

export const serviceItemListQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  kind: z.enum(SERVICE_ITEM_KINDS).optional(),
  category: z.enum(SERVICE_ITEM_CATEGORIES).optional(),
  status: z.enum(ACTIVE_STATUSES).optional(),
  /** Only items offered at this facility. */
  facilityId: z.uuid().optional(),
  /** Which list the `price` column shows. */
  priceListId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** The last item of the previous page; the list is ordered by code. */
  cursor: z.uuid().optional(),
});

const billingUnitSchema = z.string().trim().min(1).max(40);

export const createServiceItemRequestSchema = z
  .object({
    clientRequestId: z.uuid(),
    code: itemCodeTextSchema,
    name: z.string().trim().min(2).max(200),
    kind: z.enum(SERVICE_ITEM_KINDS),
    category: z.enum(SERVICE_ITEM_CATEGORIES),
    billingUnit: billingUnitSchema.default('each'),
    /** Optional first prices, each starting now. */
    initialPrices: z
      .array(z.object({ priceListId: z.uuid(), unitPrice: amountSchema }))
      .max(20)
      .default([]),
  })
  .refine((v) => categoryMatchesKind(v.category, v.kind), {
    message: 'Medicines and consumables are stock items; every other category is a service',
    path: ['category'],
  })
  .refine(
    (v) => new Set(v.initialPrices.map((p) => p.priceListId)).size === v.initialPrices.length,
    {
      message: 'Give each price list one price',
      path: ['initialPrices'],
    },
  );

/** Kind can't change: stock rows may already hang off the item. */
export const updateServiceItemRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: z.string().trim().min(2).max(200).optional(),
  category: z.enum(SERVICE_ITEM_CATEGORIES).optional(),
  billingUnit: billingUnitSchema.optional(),
});

export const deactivateRequestSchema = z.object({
  expectedVersion: z.number().int().min(1),
  reason: reasonSchema,
});

export const reactivateRequestSchema = z.object({ expectedVersion: z.number().int().min(1) });

export const addItemCodeRequestSchema = z
  .object({
    system: z.enum(ITEM_CODE_SYSTEMS),
    systemUri: z.string().trim().min(1).max(300).nullable().default(null),
    systemVersion: z.string().trim().min(1).max(60),
    code: z.string().trim().min(1).max(64),
    display: z.string().trim().min(1).max(300),
    source: z.enum(ITEM_CODE_SOURCES),
  })
  .refine((v) => codeSourceAllowed(v.system, v.source), {
    message: 'National codes come from the terminology service or an import, not typed by hand',
    path: ['source'],
  });

export const setAvailabilityRequestSchema = z
  .object({ offered: z.boolean(), reason: reasonSchema.nullable().default(null) })
  .refine((v) => v.offered || v.reason !== null, {
    message: 'Say why it is not offered here',
    path: ['reason'],
  });
